/**
 * THE LINEUP. GAME-DESIGN 13.2's in-fiction challenge, and the only automatic consequence in the
 * anti-automation ladder.
 *
 * It is a modal over the whole app rather than a screen, because it is not somewhere a player
 * goes: it is something that happens to them, once, in the middle of what they were doing. Every
 * tokened verb in the game refuses while this is up, so putting it behind navigation would be a
 * dead end.
 *
 * WHY IT LOOKS LIKE THIS. 13.2 asks for a probe that a bot "either fails or has to build a solver"
 * for, and the strongest thing this game has that a script does not is the writer. Every face on
 * this screen is a portrait somebody commissioned and every witness statement is the physical
 * description that same writer put in CONTENT-BIBLE next to it. A person reads "a jeweler's loupe
 * pushed up on his forehead", looks at six faces, and is done in three seconds. A headless client
 * has to map authored prose to an image.
 *
 * BEING WRONG COSTS NOTHING and the screen says so out loud, before the answer is given. That is
 * 13.3's argument made visible: the trust cost of a false positive exceeds the cheating cost, so
 * a probe that punishes somebody for misreading a sentence is a false-positive generator with a
 * fiction wrapped round it. What the answer produces is a timestamp.
 */

import { useEffect, useState } from 'react'
import type { Lineup as LineupData } from '@ashport/shared'
import { A } from '../art'
import { Btn, Face } from '../ui/Kit'
import { api } from '../net/api'

export function Lineup({ data, onDone }: { data: LineupData; onDone: () => void }) {
  const [picked, setPicked] = useState<number | null>(null)
  const [sent, setSent] = useState(false)
  const [result, setResult] = useState<{ correct: boolean } | null>(null)

  // The interstitial takes the keyboard. A player who was mid-keystroke somewhere else should not
  // be typing into a screen they cannot see, and the focus trap is one line here rather than a
  // hook that has to be right on every screen underneath.
  useEffect(() => {
    const prev = document.activeElement as HTMLElement | null
    return () => prev?.focus?.()
  }, [])

  async function answer() {
    if (picked === null || sent) return
    setSent(true)
    try {
      const res = await api.answerLineup(data.id, picked)
      setResult({ correct: res.correct })
    } catch {
      // A failed request leaves the modal up rather than letting the player through, because the
      // server is still refusing their actions and a dismissed modal would look like a dead game.
      setSent(false)
    }
  }

  return (
    <div
      role="dialog"
      aria-modal="true"
      aria-label="Precinct lineup"
      style={{
        position: 'fixed',
        inset: 0,
        zIndex: 200,
        display: 'grid',
        placeItems: 'center',
        background: 'rgba(6,6,8,.88)',
        backdropFilter: 'blur(3px)',
        padding: 16,
      }}
    >
      <div
        className="panel"
        data-tone="police"
        style={{ maxWidth: 760, width: '100%', maxHeight: '100%', overflow: 'auto' }}
      >
        <div style={{ padding: '18px 20px 20px' }}>
          <div
            className="t-display"
            style={{ fontSize: 28, letterSpacing: '.08em', color: 'var(--ink)' }}
          >
            THE LINEUP
          </div>
          <div className="t-body" style={{ marginTop: 8, color: 'var(--ink-dim)', lineHeight: 1.55 }}>
            {result === null ? (
              <>
                Six of them behind the glass and a witness on the other side of it. The sergeant
                reads the description out and waits. Point at one and you can go.
              </>
            ) : result.correct ? (
              <>The sergeant writes something down and waves you at the door. That is all.</>
            ) : (
              <>
                The sergeant looks at you, then at the glass, then writes something down anyway.
                Nothing turns on it. You can go.
              </>
            )}
          </div>

          {result === null && (
            <>
              <div
                className="t-body"
                style={{
                  marginTop: 14,
                  padding: '12px 14px',
                  border: '1px solid var(--brass-dim)',
                  background: 'rgba(0,0,0,.35)',
                  fontStyle: 'italic',
                  color: 'var(--ink)',
                  lineHeight: 1.6,
                }}
              >
                &ldquo;{data.subject}&rdquo;
              </div>

              <div
                style={{
                  marginTop: 16,
                  display: 'grid',
                  /*
                   * SIX ACROSS, IN ONE ROW, on anything wider than a phone.
                   *
                   * The first version used `minmax(120px, 1fr)` and the modal's 720px of content
                   * fits five of those, so it drew a row of five and an orphan underneath. A
                   * lineup is a row of people you look along; five and one reads as a mistake and
                   * makes the sixth face the odd one out before the player has read anything.
                   *
                   * 104 is the largest minimum that still fits six inside 720 with a 12px gap,
                   * and below that width it wraps to three and three, which is still a shape
                   * rather than an accident.
                   */
                  gridTemplateColumns: 'repeat(auto-fit, minmax(104px, 1fr))',
                  gap: 12,
                }}
              >
                {data.suspects.map((assetId, i) => (
                  <button
                    key={assetId}
                    type="button"
                    onClick={() => setPicked(i)}
                    aria-pressed={picked === i}
                    aria-label={`Suspect ${i + 1}`}
                    disabled={sent}
                    style={{
                      display: 'grid',
                      placeItems: 'center',
                      gap: 6,
                      padding: 8,
                      background: picked === i ? 'rgba(214,168,74,.14)' : 'transparent',
                      border: `1px solid ${picked === i ? 'var(--gold-hot)' : 'var(--brass-dim)'}`,
                      cursor: sent ? 'default' : 'pointer',
                    }}
                  >
                    <Face src={A(assetId)} alt="" size={96} ring={picked === i} />
                    <span className="t-label" style={{ fontSize: 11, color: 'var(--ink-dim)' }}>
                      {i + 1}
                    </span>
                  </button>
                ))}
              </div>

              <div
                style={{
                  marginTop: 16,
                  display: 'flex',
                  alignItems: 'center',
                  gap: 12,
                  flexWrap: 'wrap',
                }}
              >
                <Btn onClick={() => void answer()} disabled={picked === null || sent} v="gold">
                  That one
                </Btn>
                {/*
                 * Said BEFORE the answer, not after. A reassurance that only appears once you have
                 * got it wrong is not a reassurance, it is an apology, and the whole point of a
                 * low-consequence probe is that the player knows it is low-consequence while they
                 * are deciding.
                 */}
                <span className="t-body" style={{ fontSize: 12, color: 'var(--ink-dim)' }}>
                  Getting it wrong costs you nothing. Nothing you were doing is cancelled.
                </span>
              </div>
            </>
          )}

          {result !== null && (
            <div style={{ marginTop: 18 }}>
              <Btn onClick={onDone} v="gold">
                Out the side door
              </Btn>
            </div>
          )}
        </div>
      </div>
    </div>
  )
}
